import React, { useState } from "react";
import API from "../../../utils/API";
import classnames from "classnames";
import CryptoTool from "../../../utils/CryptoTool";
import { calculateDeliveryDate, calculateScheduledDate, calculateLimitEditDate } from "../../../utils/functions";
import { logSentryError } from "../../../utils/sentryLogger";
import styled from "styled-components";
import DeliveryMealList from "./../../organisms/DeliveryMealList";
import DeliveryMealSettings from "./../../organisms/DeliveryMealSettings";
import { ReactComponent as Toggle } from "./../../atoms/icons/arrow.svg";
import { queryURL, dietTypeSubtitle } from "./utils";

const Wrap = styled.div`
  margin-bottom: 25px;
  padding: 30px 30px 10px 30px;
  border: solid 1px #e6e6e6;
  border-radius: 3px;
  transition: 0.3s;

  &:hover {
    box-shadow: 2px 2px 10px rgb(0 0 0 / 15%);
    transition: 0.3s;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &.delivery-item--skipped .delivery-item__header-content h2,
  &.delivery-item--skipped .delivery-item__header-content p:not(.expander-p),
  &.delivery-item--skipped .delivery-item__ct,
  &.delivery-item--skipped .delivery-item__meal-list,
  &.delivery-item--skipped .delivery-item__markup {
    opacity: 0.3;
  }

  &:not(.delivery-item--expanded) .delivery-item__body {
    display: none;
  }

  &.delivery-item--expanded .delivery-item__body {
    display: block;
  }

  &.delivery-item--order .delivery-item__markup-sub {
    display: none;
  }
`;

const SkippedMark = styled.button`
  color: #fff;
  border: none;
  font-size: 13px;
  font-weight: bold;
  padding: 4px 8px;
  font-stretch: normal;
  font-style: normal;
  background-color: #f15a29;
  border-radius: 7px;
  line-height: 1.67;
  letter-spacing: normal;
  text-align: center;
`;

const Header = styled.div`
  @media (min-width: 990px) {
    display: flex;
    justify-content: space-between;
  }
`;
const HeaderContent = styled.div`
  padding: 0 0 16px 0;

  @media (min-width: 990px) {
    border-bottom: none;
  }

  p {
    font-size: 16px;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
  }
`;

const Menu = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding: 16px 0 0 0;
  clear: both;

  @media (min-width: 990px) {
    text-align: right;
  }
`;

const Title = styled.h2`
  margin-bottom: 16px;
  font-size: 28px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.14;
`;

const SubTitle = styled.h2`
  margin-bottom: 16px;
  font-size: 18px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.14;
  letter-spacing: -0.7px;
  color: var(--color-secondary);

  &.active {
    color: var(--color-primary);
  }
`;

const Button = styled.button`
  position: relative;
  margin-right: 17px;
  margin-bottom: 16px;
  background: none;
  border: none;
  background: #fff;
  padding: 12px 20px;
  border: 1px solid var(--color-primary);
  border-radius: 20px;
  color: var(--color-primary);
  font-size: 11px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.45;
  letter-spacing: 0.6px;
  text-align: center;
  text-transform: uppercase;
  outline: none;
  box-shadow: none;
  cursor: pointer;
  user-select: none;

  &:focus,
  &:hover,
  &:active {
    outline: none;
    box-shadow: none;
  }

  &:hover:not(:disabled) {
    background-color: var(--color-primary);
    color: #fff;
  }

  &:disabled {
    opacity: 0.3;
  }

  &:last-child {
    margin-right: 0;
  }

  @media (min-width: 990px) {
    margin-right: 0;

    & + button {
      margin-left: 17px;
    }
  }

  @keyframes ld-spin {
    0% {
      animation-timing-function: cubic-bezier(0.5856, 0.0703, 0.4143, 0.9297);
      transform: rotate(0);
    }

    100% {
      transform: rotate(360deg);
    }
  }

  span {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    margin: -10px;
    opacity: 0;
    z-index: -1;
    transition: all 0.3s;
    transition-timing-function: ease-in;
    animation: ld-spin 1s infinite linear;

    &::after {
      content: " ";
      display: block;
      width: 40px;
      height: 40px;
      box-sizing: border-box;
      border-radius: 50%;
      border: 5px solid var(--color-primary);
      border-left-color: transparent;
      transform-origin: 0 0;
      transform: translateZ(0) scale(0.5);
      backface-visibility: hidden;
    }
  }

  &.loading {
    span {
      opacity: 1;
      z-index: auto;
      visibility: visible;
    }

    b {
      opacity: 0.3;
    }
  }
`;

const Body = styled.div``;

const Expander = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  background: #fff;
  padding: 0;
  color: var(--color-primary);
  font-size: 16px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.45;
  text-align: center;
  outline: none;
  box-shadow: none;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: var(--color-primary);
  }

  &.active svg {
    transform: rotate(180deg);
  }
`;

const apiUrl = CryptoTool.getApiUrl();

function DeliveryItem(props) {
  const {
    scheduledAt,
    processedAt,
    main_title,
    shippingAddress,
    meals,
    status,
    id,
    excludeCodes,
    exclusions,
    isOrder,
    isOldUser,
    customerId,
  } = props;

  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleChangeQuantity = (deliveryId, mainTitle) => {
    props.handleChangeQuantity(deliveryId, mainTitle);
  };

  const handleEditDelivery = (deliveryId) => {
    props.handleEditDelivery(deliveryId, main_title);
  };

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const deliveryQuery = (data) => {
    const customerEmail = window.theme.customerEmail;

    return API.post(CryptoTool.cryptUrl(data.url, customerEmail, true), JSON.stringify(data.charges)).catch((error) => {
      logSentryError(error);
    });
  };

  const toggleDeliveryStatus = (status) => {
    const charges = meals.map((meal) => {
      return {
        chargeId: id,
        subscriptionId: meal.subscriptionId,
      };
    });

    if (status === "QUEUED" || status === "SKIPPED") {
      let url = queryURL(status, apiUrl, customerId);

      setLoading(true);

      deliveryQuery({
        url,
        charges,
      }).then(() => {
        setLoading(false);
        window.location.reload();
      });
    }
  };

  const deliveryDate = isOrder ? calculateDeliveryDate(processedAt, "long") : calculateScheduledDate(scheduledAt, processedAt, "long");

  const editLimitDate = isOrder ? calculateLimitEditDate(processedAt) : calculateLimitEditDate(scheduledAt);

  const isSkipped = status === "SKIPPED" ? true : false;

  const generateSkipButtonContent = (isSkipped) => {
    const text = isSkipped ? "Unskip this WEEK’S delivery" : "Skip this WEEK’S delivery";

    return (
      <>
        <span></span>
        <b>{text}</b>
      </>
    );
  };

  const generateStatusText = (isOrder, editLimitDate) => {
    return isOrder ? (
      <p>This order is in progress and cannot be edited.</p>
    ) : (
      <p>{"You can make changes to this delivery until 11:59 PM EST " + editLimitDate}.</p>
    );
  };

  const generateIsSkipped = (isSkipped) => {
    return isSkipped ? (
      <div>
        <SkippedMark> Skipped </SkippedMark>
      </div>
    ) : null;
  };

  return (
    <Wrap
      className={classnames(
        "delivery-item",
        { "delivery-item--skipped": isSkipped },
        { "delivery-item--expanded": expanded },
        { "delivery-item--order": isOrder }
      )}
    >
      <Header>
        <HeaderContent className="delivery-item__header-content">
          <p>Delivery by: <Title>{deliveryDate}</Title></p>
          <SubTitle className={classnames({ active: dietTypeSubtitle(meals) === "Mediterranean" })}>{dietTypeSubtitle(meals)}</SubTitle>
          {generateStatusText(isOrder, editLimitDate)}
          <p className="expander-p">
            <Expander onClick={handleExpand} className={classnames({ active: expanded })}>
              <b>{expanded ? "Click to Hide Selections" : "Click to Show Selections"}</b>
              <Toggle />
            </Expander>
          </p>
        </HeaderContent>
        {generateIsSkipped(isSkipped)}
      </Header>
      <Body className="delivery-item__body">
        <Menu className="delivery-item__menu">
          <DeliveryMealSettings
            shippingAddress={shippingAddress}
            meals={meals}
            isSkipped={isSkipped}
            id={id}
            handleChangeQuantity={handleChangeQuantity}
            excludeCodes={excludeCodes}
            exclusions={exclusions}
            isOrder={isOrder}
            main_title={dietTypeSubtitle(meals)}
          />
          {!isOldUser && !isOrder && (
            <Button
              onClick={() => {
                toggleDeliveryStatus(status);
              }}
              className={classnames("delivery-item__state-btn", { "loading ": loading })}
              disabled={loading || isOrder ? true : false}
            >
              {generateSkipButtonContent(isSkipped)}
            </Button>
          )}
          {!isOrder && (
            <Button
              disabled={isSkipped || isOrder}
              onClick={() => {
                handleEditDelivery(id);
              }}
            >
              <b>Edit Meal Selections</b>
            </Button>
          )}
        </Menu>

        <DeliveryMealList meals={meals} />
      </Body>
    </Wrap>
  );
}

export default DeliveryItem;
